/**
 * AnalyticsDashboard - Marketing statistics overview
 * Shows: campaigns, publications, leads, AI tip
 */
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import PanelLayout from '@/components/PanelLayout';
import AssistantPanel from '@/components/AssistantPanel';
import { Button } from '@/components/ui/button';
import { API } from '@/utils/api';
import {
  BarChart3, Loader2, Rocket, Send, Users, Sparkles,
  Calendar, TrendingUp, Clock, AlertCircle, ArrowRight,
  CheckCircle2, Target
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

function formatDate(isoStr) {
  if (!isoStr) return '';
  try {
    const d = new Date(isoStr);
    return d.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return ''; }
}

function StatCard({ icon: Icon, label, value, subtitle, color, bg, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="rounded-2xl border p-5"
      style={{ background: 'white', borderColor: 'var(--apisai-border)' }}
      data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: bg }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        {subtitle && (
          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ background: bg, color }}>{subtitle}</span>
        )}
      </div>
      <p className="text-2xl font-bold mb-0.5" style={{ color: 'var(--apisai-text)' }}>{value}</p>
      <p className="text-xs" style={{ color: 'var(--apisai-text-muted)' }}>{label}</p>
    </motion.div>
  );
}

function AiTipCard({ tip, onAction }) {
  if (!tip) return null;

  const priorityColors = {
    high: { bg: '#FEF2F2', border: '#EF444430', color: '#DC2626', icon: AlertCircle },
    medium: { bg: '#FFF7ED', border: '#F9731630', color: '#EA580C', icon: Sparkles },
    low: { bg: '#F0FDF4', border: '#10B98130', color: '#059669', icon: CheckCircle2 },
  };
  const p = priorityColors[tip.priority] || priorityColors.medium;
  const TipIcon = p.icon;

  const actionLabels = {
    create_campaign: 'Przygotuj kampanie',
    publish: 'Opublikuj post',
    share: 'Udostepnij kampanie',
    schedule: 'Zaplanuj publikacje',
    continue: 'Przejdź do Boostera',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="rounded-2xl border p-5"
      style={{ background: p.bg, borderColor: p.border }}
      data-testid="ai-tip-card"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: p.color + '15' }}>
          <TipIcon className="w-4 h-4" style={{ color: p.color }} />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold mb-1" style={{ color: p.color }}>{tip.title}</p>
          <p className="text-xs leading-relaxed mb-3" style={{ color: p.color + 'CC' }}>{tip.message}</p>
          <Button
            onClick={onAction}
            className="rounded-xl h-8 text-xs px-3 font-medium text-white"
            style={{ background: p.color }}
            data-testid="ai-tip-action"
          >
            {actionLabels[tip.action] || 'Przejdź'} <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

function PublicationBreakdown({ publications }) {
  if (!publications) return null;

  const items = [
    { label: 'Opublikowane', value: publications.published, color: '#10B981', bg: '#F0FDF4' },
    { label: 'Zaplanowane', value: publications.scheduled, color: '#F59E0B', bg: '#FFFBEB' },
    { label: 'Błędy', value: publications.failed, color: '#EF4444', bg: '#FEF2F2' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.25 }}
      className="rounded-2xl border p-5"
      style={{ background: 'white', borderColor: 'var(--apisai-border)' }}
      data-testid="publication-breakdown"
    >
      <div className="flex items-center gap-2 mb-4">
        <Clock className="w-4 h-4" style={{ color: 'var(--apisai-primary)' }} />
        <h3 className="text-sm font-semibold" style={{ color: 'var(--apisai-text)' }}>Status publikacji</h3>
      </div>
      <div className="space-y-2.5">
        {items.map(item => (
          <div key={item.label} className="flex items-center gap-3">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs" style={{ color: 'var(--apisai-text-secondary)' }}>{item.label}</span>
                <span className="text-xs font-bold" style={{ color: item.color }}>{item.value}</span>
              </div>
              <div className="h-1.5 rounded-full" style={{ background: '#F3F4F6' }}>
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    background: item.color,
                    width: publications.total > 0 ? `${(item.value / publications.total) * 100}%` : '0%',
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

function LeadsList({ leads }) {
  if (!leads || leads.total === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="rounded-2xl border p-5"
      style={{ background: 'white', borderColor: 'var(--apisai-border)' }}
      data-testid="leads-overview"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4" style={{ color: '#8B5CF6' }} />
          <h3 className="text-sm font-semibold" style={{ color: 'var(--apisai-text)' }}>Leady</h3>
        </div>
        {leads.new > 0 && (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: '#8B5CF620', color: '#8B5CF6' }}>
            {leads.new} nowych
          </span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="text-center p-2.5 rounded-xl" style={{ background: '#F5F3FF' }}>
          <p className="text-lg font-bold" style={{ color: '#8B5CF6' }}>{leads.total}</p>
          <p className="text-[10px]" style={{ color: '#8B5CF6AA' }}>Łącznie</p>
        </div>
        <div className="text-center p-2.5 rounded-xl" style={{ background: '#FFFBEB' }}>
          <p className="text-lg font-bold" style={{ color: '#F59E0B' }}>{leads.new}</p>
          <p className="text-[10px]" style={{ color: '#F59E0BAA' }}>Nowe</p>
        </div>
        <div className="text-center p-2.5 rounded-xl" style={{ background: '#EBF5FF' }}>
          <p className="text-lg font-bold" style={{ color: '#1877F2' }}>{leads.this_week}</p>
          <p className="text-[10px]" style={{ color: '#1877F2AA' }}>Ten tydzień</p>
        </div>
      </div>
      {leads.latest && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--apisai-border)' }}>
          <p className="text-[11px]" style={{ color: 'var(--apisai-text-muted)' }}>
            Ostatni lead: <strong>{leads.latest.name}</strong> · {formatDate(leads.latest.created_at)}
          </p>
        </div>
      )}
    </motion.div>
  );
}

export default function AnalyticsDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');

  useEffect(() => {
    axios.get(`${API}/booster/analytics`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleTipAction = () => {
    const action = data?.ai_tip?.action;
    if (action === 'create_campaign' || action === 'publish' || action === 'continue') navigate('/panel/booster');
    else if (action === 'share') navigate('/panel/biblioteka');
    else if (action === 'schedule') navigate('/panel/booster');
    else navigate('/panel/booster');
  };

  if (loading) {
    return <PanelLayout><div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--apisai-primary)' }} /></div></PanelLayout>;
  }

  return (
    <PanelLayout>
      <div className="max-w-[1200px] mx-auto" data-testid="analytics-dashboard">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--apisai-primary)' }}>
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold" style={{ color: 'var(--apisai-text)' }}>Analityka</h1>
              <p className="text-sm" style={{ color: 'var(--apisai-text-secondary)' }}>Sprawdź wyniki swoich działań marketingowych</p>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* CENTER COLUMN */}
          <div className="lg:col-span-8 space-y-5">

        {/* Main stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            icon={Rocket}
            label="Kampanie"
            value={data?.campaigns?.total || 0}
            subtitle={data?.campaigns?.this_week > 0 ? `+${data.campaigns.this_week} ten tydz.` : null}
            color="#F97316"
            bg="#FFF7ED"
            delay={0}
          />
          <StatCard
            icon={Send}
            label="Publikacje"
            value={data?.publications?.published || 0}
            subtitle={data?.publications?.scheduled > 0 ? `${data.publications.scheduled} zaplan.` : null}
            color="#1877F2"
            bg="#EBF5FF"
            delay={0.05}
          />
          <StatCard
            icon={Users}
            label="Leady"
            value={data?.leads?.total || 0}
            subtitle={data?.leads?.new > 0 ? `${data.leads.new} nowych` : null}
            color="#8B5CF6"
            bg="#F5F3FF"
            delay={0.1}
          />
          <StatCard
            icon={Target}
            label="Skuteczność"
            value={data?.leads?.total > 0 && data?.campaigns?.total > 0
              ? `${Math.round((data.leads.total / data.campaigns.total) * 100)}%`
              : '—'}
            subtitle={data?.campaigns?.latest_date ? formatDate(data.campaigns.latest_date) : null}
            color="#10B981"
            bg="#F0FDF4"
            delay={0.15}
          />
        </div>

        {/* AI Tip */}
        {data?.ai_tip && (
          <div className="mb-5">
            <AiTipCard tip={data.ai_tip} onAction={handleTipAction} />
          </div>
        )}

        {/* Publication breakdown + Leads */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <PublicationBreakdown publications={data?.publications} />
          <LeadsList leads={data?.leads} />
        </div>

          </div>

          {/* RIGHT COLUMN — Assistant */}
          <div className="lg:col-span-4" data-testid="analytics-right-column">
            <div className="sticky top-[80px]">
              <AssistantPanel
                whatNow="Sprawdź, które kampanie przyniosły najwięcej zapytań i powiel ten sukces."
              />
            </div>
          </div>
        </div>
      </div>
    </PanelLayout>
  );
}
